import { AsyncPipe, TitleCasePipe, NgClass, CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { doc, deleteDoc, Firestore } from '@angular/fire/firestore';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { Event, EventStatus, EventStats } from '../../../../models/event.model';
import { Group } from '../../../../models/group.model';
import {
  MembershipStatus,
  MemberType,
} from '../../../../models/membership.status';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import * as eventActions from '../../../../state/actions/event.actions';
import { AppState } from '../../../../state/appstate';
import * as eventSelector from '../../../../state/selectors/event.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnRouteComponent } from '../../../components/buttons/btn-route/btn-route.component';
import { DlgSelectOptionComponent } from '../../../components/dialogs/dlg-select-option/dlg-select-option.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import {
  ChipColor,
  ChipComponent,
} from '../../../components/inputs/chip/chip.component';
import { ViewLocationComponent } from '../../../components/maps/view-location/view-location.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { AuthService } from '../../../../services/auth.service';
import { EventService } from '../../../../services/event.service';
import { DlgVolunteersComponent } from '../dlg-volunteers/dlg-volunteers.component';

@Component({
  selector: 'app-detail-event',
  standalone: true,
  imports: [
    AsyncPipe,
    TitleCasePipe,
    NgClass,
    CommonModule,
    MatDialogModule,
    ViewLocationComponent,
    BtnRouteComponent,
    BtnBackComponent,
    MomentPipe,
    ChipComponent,
    BtnFabComponent,
    DlgConfirmComponent,
    DlgVolunteersComponent,
  ],
  templateUrl: './detail-event.component.html',
  styleUrls: ['./detail-event.component.css'],
})
export class DetailEventComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  private dialog: MatDialog = inject(MatDialog);
  private readonly afs = inject(Firestore);
  private auth = inject(AuthService);
  private eventService = inject(EventService);
  private db = inject(AngularFirestore);

  group: Group | null | undefined;
  groupSub: Subscription | undefined;

  eventSub: Subscription | undefined;
  event: Event | null | undefined;

  groupId: string | null = null;
  eventId: string | null = null;

  eventStatus = EventStatus;
  chipColor = ChipColor;

  myRoles$: Observable<any> = new Observable();
  isAdmin = false;
  rolesSub: Subscription | undefined;

  membershipStatus = MembershipStatus;
  memberType = MemberType;

  // RSVP Properties
  userRsvpStatus: string | undefined;
  rsvpGuests = 0;
  tempRsvpStatus: string | undefined;
  tempRsvpGuests = 0;
  showRsvpBottomSheet = false;
  eventStats: EventStats | null | undefined;
  eventStatsSub: Subscription | undefined;

  volunteeringDetails: Record<string, { volunteers: any[], volunteersRequired: number, volunteerCriteria?: string }> = {};
  volunteeringSub: Subscription | undefined;
  volunteeringProgress: Record<string, boolean> = {};

  get isAuthorized(): boolean {
    return this.isAdmin && this.group?.memberStatus === MembershipStatus.active;
  }

  constructor(
    private store: Store<AppState>,
    private _activatedRoute: ActivatedRoute,
  ) {
    this._activatedRoute.paramMap.subscribe((params) => {
      this.groupId = params.get('id');
      this.eventId = params.get('eventId');
    });

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );
  }
  ngOnInit(): void {
    this.groupSub = this.store
      .pipe(select(groupSelector.getGroupById(this.groupId!)))
      .subscribe((group) => {
        this.group = group;
      });
     this.eventSub = this.store
      .pipe(select(eventSelector.getEventById(this.eventId!)))
      .subscribe((event) => {
        this.event = event;
        this.loadUserRsvpStatus();
        this.updateVolunteersCountIfNeeded();
      });
    this.rolesSub = this.myRoles$.subscribe((roles) => {
      this.isAdmin = !!roles?.admin;
    });

    // Real-time Event Stats Subscription
    if (this.eventId) {
      this.eventStatsSub = this.db
        .collection('event_stats')
        .doc<EventStats>(this.eventId)
        .valueChanges()
        .subscribe((stats) => {
          this.eventStats = stats;
        });
    }

    if (this.groupId && this.eventId) {
      this.volunteeringSub = this.eventService
        .getVolunteeringDetails(this.groupId, this.eventId)
        .subscribe((calendarEvents) => {
          const details: Record<string, { volunteers: any[], volunteersRequired: number, volunteerCriteria?: string }> = {};
          calendarEvents.forEach((e) => {
            const docId = e.id;
            const prefix = `event_${this.eventId}_`;
            const subEventId = docId.startsWith(prefix) ? docId.substring(prefix.length) : docId.split('_').pop() || '';
            details[subEventId] = {
              volunteers: e.volunteers || [],
              volunteersRequired: e.volunteersRequired || 0,
              volunteerCriteria: e.volunteerCriteria || '',
            };
          });
          this.volunteeringDetails = details;
          this.updateVolunteersCountIfNeeded();
        });
    }
  }

  ngOnDestroy(): void {
    this.groupSub?.unsubscribe();
    this.eventSub?.unsubscribe();
    this.rolesSub?.unsubscribe();
    this.eventStatsSub?.unsubscribe();
    this.volunteeringSub?.unsubscribe();
  }

  loadUserRsvpStatus() {
    const userId = this.auth.getUid();
    if (userId && this.eventId) {
      this.eventService.getAttendeeStatus(this.eventId, userId).subscribe((attendee) => {
        if (attendee) {
          this.userRsvpStatus = attendee.status;
          this.rsvpGuests = attendee.additionalGuests || 0;
        } else {
          this.userRsvpStatus = undefined;
          this.rsvpGuests = 0;
        }
      });
    }
  }

  openRsvpBottomSheet() {
    this.tempRsvpStatus = this.userRsvpStatus;
    this.tempRsvpGuests = this.rsvpGuests;
    this.showRsvpBottomSheet = true;
  }

  closeRsvpBottomSheet() {
    this.showRsvpBottomSheet = false;
  }

  adjustTempGuests(diff: number) {
    const newVal = this.tempRsvpGuests + diff;
    if (newVal >= 0 && newVal <= 10) {
      this.tempRsvpGuests = newVal;
    }
  }

  selectTempStatus(status: string) {
    this.tempRsvpStatus = status;
  }

  async updateRsvp(status: string | undefined, guestsToWrite: number) {
    if (!status) return;
    const userId = this.auth.getUid();
    const userName = this.auth.getUserName() || 'Anonymous';
    const photoUrl = this.auth.getUserPhotoURL() || null;

    if (userId && this.eventId) {
      const oldStatus = this.userRsvpStatus;
      const oldGuests = this.rsvpGuests;
      
      const finalGuests = status === 'attending' ? guestsToWrite : 0;
      
      this.userRsvpStatus = status; // Optimistic update
      this.rsvpGuests = finalGuests; // Optimistic update
      this.showRsvpBottomSheet = false;

      // Optimistically update counts locally in eventStats
      let attendingDiff = 0;
      let tentativeDiff = 0;
      let declinedDiff = 0;

      if (oldStatus === 'attending') attendingDiff -= (1 + oldGuests);
      else if (oldStatus === 'tentative') tentativeDiff -= 1;
      else if (oldStatus === 'declined') declinedDiff -= 1;

      if (status === 'attending') attendingDiff += (1 + finalGuests);
      else if (status === 'tentative') tentativeDiff += 1;
      else if (status === 'declined') declinedDiff += 1;

      if (this.eventStats) {
        this.eventStats = {
          ...this.eventStats,
          attendingCount: (this.eventStats.attendingCount || 0) + attendingDiff,
          tentativeCount: (this.eventStats.tentativeCount || 0) + tentativeDiff,
          declinedCount: (this.eventStats.declinedCount || 0) + declinedDiff,
        };
      } else {
        this.eventStats = {
          attendingCount: attendingDiff > 0 ? attendingDiff : 0,
          tentativeCount: tentativeDiff > 0 ? tentativeDiff : 0,
          declinedCount: declinedDiff > 0 ? declinedDiff : 0,
        };
      }

      try {
        await this.eventService.updateAttendeeStatus(
          this.eventId,
          userId,
          userName,
          photoUrl,
          status,
          finalGuests
        );
      } catch (err) {
        console.error('Failed to update RSVP status:', err);
        // Revert on error
        this.userRsvpStatus = oldStatus;
        this.rsvpGuests = oldGuests;
        if (this.eventStats) {
          this.eventStats = {
            ...this.eventStats,
            attendingCount: (this.eventStats.attendingCount || 0) - attendingDiff,
            tentativeCount: (this.eventStats.tentativeCount || 0) - tentativeDiff,
            declinedCount: (this.eventStats.declinedCount || 0) - declinedDiff,
          };
        }
      }
    }
  }

  changeStatusDialog() {
    let data: any = [];
    switch (this.event?.status) {
      case EventStatus.active:
        data = [EventStatus.active, EventStatus.cancelled];
        break;
      case EventStatus.cancelled:
        data = [EventStatus.active, EventStatus.cancelled];
        break;
      case EventStatus.request:
        data = [EventStatus.request, EventStatus.active, EventStatus.rejected];
        break;
      case EventStatus.rejected:
        data = [
          EventStatus.active,
          EventStatus.cancelled,
          EventStatus.rejected,
        ];
        break;
      default:
        data = [];
        break;
    }
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Update Event Status',
      description: 'Select the new event status and proceed',
      options: data,
      action: 'Update',
      selectedOption: this.event?.status,
    };
    const dialogRef = this.dialog.open(DlgSelectOptionComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined && this.event) {
        return this.store.dispatch(
          eventActions.updateStatus({
            id: this.event?.id!,
            event: {
              requestedBy: this.event.requestedBy,
              requestedById: this.event.requestedById,
              status: result,
            } as Event,
          }),
        );
      }
      return;
    });
  }

  openVolunteersDialog(subEvent: any) {
    const details = this.volunteeringDetails[subEvent.id];
    let dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: subEvent.name,
      volunteers: details ? [...details.volunteers] : [],
      volunteersRequired: details ? details.volunteersRequired : (subEvent.volunteersRequired || 0),
      groupId: this.groupId,
      eventId: this.eventId,
      subEventId: subEvent.id
    };
    this.dialog.open(DlgVolunteersComponent, dialogConfig);
  }

  getSortedSubEvents(events: any): any[] {
    if (!events) return [];
    return Object.entries(events)
      .map(([id, val]: [string, any]) => ({
        id,
        ...val,
      }))
      .sort((a: any, b: any) => {
        const aTime = a.date?.toDate ? a.date.toDate().getTime() : new Date(a.date).getTime();
        const bTime = b.date?.toDate ? b.date.toDate().getTime() : new Date(b.date).getTime();
        return aTime - bTime;
      });
  }

  async deleteSubEvent(subEventId: string) {
    if (!this.event || !this.groupId) return;

    // 1. Remove from parent event document
    const updatedEvents = { ...this.event.events };
    delete updatedEvents[subEventId];

    const updatedEvent: Event = {
      ...this.event,
      events: Object.keys(updatedEvents).length > 0 ? updatedEvents : undefined,
    };

    // Dispatch update action
    this.store.dispatch(
      eventActions.update({
        id: this.event.id!,
        event: updatedEvent,
      })
    );

    // 2. Delete from events_calendar subcollection in the background
    try {
      const calendarDocRef = doc(
        this.afs,
        'groups',
        this.groupId,
        'events_calendar',
        `event_${this.event.id}_${subEventId}`
      );
      await deleteDoc(calendarDocRef);
    } catch (err) {
      console.error('Failed to delete sub-event from calendar:', err);
    }
  }

  hasUserVolunteered(subEventId: string): boolean {
    const userId = this.auth.getUid();
    const details = this.volunteeringDetails[subEventId];
    if (!userId || !details || !details.volunteers) return false;
    return details.volunteers.some((v: any) => v.uid === userId);
  }

  showErrorDialog(message: string) {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: 'Volunteering Error',
      description: message,
      showType: 'error',
      showCancel: false,
      action: 'OK',
    };
    this.dialog.open(DlgConfirmComponent, dialogConfig);
  }

  async volunteer(subEventId: string) {
    const userId = this.auth.getUid();
    const userName = this.auth.getUserName() || 'Anonymous';
    const photoUrl = this.auth.getUserPhotoURL() || null;
    if (!userId || !this.groupId || !this.eventId || !this.event || !this.event.events) return;

    const subEvent = this.event.events[subEventId];
    if (!subEvent) return;

    this.volunteeringProgress[subEventId] = true;
    try {
      const calendarEventId = `event_${this.eventId}_${subEventId}`;
      await this.eventService.volunteerForEvent(
        this.groupId,
        calendarEventId,
        {
          uid: userId,
          name: userName,
          photoUrl: photoUrl,
        },
        {
          eventId: this.eventId,
          eventName: this.event.name || '',
          eventDescription: this.event.description || '',
          eventImage: this.event.bannerImage || null,
          subEventName: subEvent.name,
          subEventDate: subEvent.date,
          volunteersRequired: subEvent.volunteersRequired || 0,
          volunteerCriteria: subEvent.volunteerCriteria || '',
        }
      );
    } catch (err: any) {
      console.error('Failed to volunteer:', err);
      this.showErrorDialog(err.message || 'Failed to volunteer');
    } finally {
      this.volunteeringProgress[subEventId] = false;
    }
  }

  async unvolunteer(subEventId: string) {
    const userId = this.auth.getUid();
    if (!userId || !this.groupId || !this.eventId) return;

    this.volunteeringProgress[subEventId] = true;
    try {
      const calendarEventId = `event_${this.eventId}_${subEventId}`;
      await this.eventService.unvolunteerForEvent(this.groupId, calendarEventId, userId, this.eventId);
    } catch (err: any) {
      console.error('Failed to unvolunteer:', err);
      this.showErrorDialog(err.message || 'Failed to unvolunteer');
    } finally {
      this.volunteeringProgress[subEventId] = false;
    }
  }

  hasVolunteeringSessions(): boolean {
    if (!this.event || !this.event.events) return false;
    return Object.values(this.event.events).some((e: any) => e.requestVolunteers);
  }

  updateVolunteersCountIfNeeded() {
    if (!this.event || !this.event.id || !this.event.events) return;
    
    const updatedEventsMap = JSON.parse(JSON.stringify(this.event.events));
    let hasChanges = false;
    let allSessionsLoaded = true;
    
    for (const subEventId of Object.keys(updatedEventsMap)) {
      const subEvent = updatedEventsMap[subEventId];
      if (subEvent.requestVolunteers) {
        const details = this.volunteeringDetails[subEventId];
        if (details) {
          const actualCount = details.volunteers?.length || 0;
          if (subEvent.volunteersCount !== actualCount) {
            subEvent.volunteersCount = actualCount;
            hasChanges = true;
          }
        } else {
          allSessionsLoaded = false;
        }
      }
    }
    
    if (allSessionsLoaded && hasChanges) {
      this.event.events = updatedEventsMap;
      this.eventService.updateSubEvents(this.event.id, updatedEventsMap).catch(err => {
        console.error('Failed to sync sub-events volunteersCount:', err);
      });
    }
  }
}
